import React, {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { ContextMenu } from "./ContextMenu";
import { MenuOptionType } from "./models/MenuOptionType";

type ContextMenuConfig = {
	id: string;
	getOptions: (data: any) => MenuOptionType[];
};

interface ContextMenuMESFContextType {
	show: boolean;
	position: { x: number; y: number };
	options: MenuOptionType[];
	showContextMenu: (e: React.MouseEvent, data: any, configId: string) => void;
	registerConfig: (config: ContextMenuConfig) => void;
	onClickOutside: () => void;
}

const ContextMenuMESFContext = createContext<ContextMenuMESFContextType>({
	show: false,
	position: { x: 0, y: 0 },
	options: [],
	showContextMenu: () => {},
	registerConfig: () => {},
	onClickOutside: () => {},
});

export const ContextMenuMESFProvider = ({
	children,
}: { children: ReactNode }) => {
	const [show, setShow] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [options, setOptions] = useState<MenuOptionType[]>([]);
	const [configs, setConfigs] = useState<{ [key: string]: ContextMenuConfig }>(
		{},
	);
	const currentDataRef = useRef<any>(null);
	const currentConfigIdRef = useRef<string | null>(null);

	const registerConfig = (config: ContextMenuConfig) => {
		setConfigs((prev) => ({ ...prev, [config.id]: config }));
	};

	const showContextMenu = (
		e: React.MouseEvent,
		data: any,
		configId: string,
	) => {
		e.preventDefault();
		e.stopPropagation();
		setShow(true);
		setPosition({ x: e.clientX, y: e.clientY });
		currentDataRef.current = data;
		currentConfigIdRef.current = configId;
		if (configs[configId]) {
			setOptions(configs[configId].getOptions(data));
		} else {
			console.warn(`No config found for id: ${configId}`);
			setOptions([]);
		}
	};

	const onClickOutside = () => {
		setShow(false);
	};

	useEffect(() => {
		const handleGlobalClick = (e: MouseEvent) => {
			if (show) {
				onClickOutside();
			}
		};

		document.addEventListener("click", handleGlobalClick);

		return () => {
			document.removeEventListener("click", handleGlobalClick);
		};
	}, [show]);

	const contextValue: ContextMenuMESFContextType = {
		show,
		position,
		options,
		showContextMenu,
		registerConfig,
		onClickOutside,
	};

	return (
		<ContextMenuMESFContext.Provider value={contextValue}>
			{children}
			<ContextMenu />
		</ContextMenuMESFContext.Provider>
	);
};

export const useContextMenuMESF = () => useContext(ContextMenuMESFContext);
